import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { logout as logoutNoClient } from '@/api/turbopro';
import { sessao, limparSessao } from '@/auth/session-store';

export interface UsuarioLogado {
  email: string;
  cnpj: string;
  empresa: string;
}

interface AuthContextType {
  user: UsuarioLogado | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Recarrega `user` a partir do session-store (chamado pelo login). */
  refrescarUsuario: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function usuarioDaSessao(): UsuarioLogado | null {
  const s = sessao();
  if (!s.logado || !s.email) return null;
  return { email: s.email, cnpj: s.cnpjGestor ?? '', empresa: s.empresa ?? '' };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UsuarioLogado | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUser(usuarioDaSessao());
    setIsLoading(false);
  }, []);

  const refrescarUsuario = useCallback(() => {
    setUser(usuarioDaSessao());
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    limparSessao();
    void logoutNoClient();
  }, []);

  const isAuthenticated = !!user && !isLoading;

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, refrescarUsuario, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
